import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"

import { sanitize } from "../../utils/utils"
import FooterSection from "../WebsiteBuilderV2/components/FooterSection"
import HeaderSection from "../WebsiteBuilderV2/components/HeaderSection"
import { formatHeadingFontFamily } from "../WebsiteBuilderV2/utils/fontUtils"
import { scopePath } from "../WebsiteBuilderV2/utils/urlUtils"
import { MinimalWebsiteBuilderV2Provider } from "../WebsiteBuilderV2/WebsiteBuilderV2Context"

import { formatBlogPostDate } from "./blogDateUtils"
import BlogIndexHeader from "./BlogIndexHeader"
import {
  collectBlogTags,
  filterPostsByTag,
  formatTagLabel,
  readBlogTagFromUrl,
  writeBlogTagToUrl
} from "./blogTagUtils"

const tagPillStyle = (textDark) => ({
  backgroundColor: "#e4eff6",
  color: textDark
})

const BlogIndexPage = ({ practice, posts, theme, deviceType = "desktop", blogPageTitle }) => {
  const headingFont = formatHeadingFontFamily(theme?.fonts?.heading)
  const textDark = theme?.colors?.textDark || "#55433f"
  const accentColor = theme?.colors?.primary || theme?.colors?.primaryDark || "#0d9488"
  const headerBackground = theme?.colors?.bgLight || theme?.colors?.background || "#f4f7f9"
  const pageTitle = blogPageTitle || practice?.blogPageTitle || "Blog"

  const allTags = useMemo(() => collectBlogTags(posts), [posts])
  const [activeTag, setActiveTag] = useState(() => readBlogTagFromUrl())

  useEffect(() => {
    if (activeTag && !allTags.includes(activeTag)) {
      setActiveTag(null)
      writeBlogTagToUrl(null)
    }
  }, [activeTag, allTags])

  const filteredPosts = useMemo(() => filterPostsByTag(posts, activeTag), [posts, activeTag])

  const selectTag = (tag) => {
    setActiveTag(tag)
    writeBlogTagToUrl(tag)
  }

  const headerSection = { id: "blog-header", settings: { showNavigation: true, showCTA: true } }
  const footerSection = { id: "blog-footer", settings: {} }

  return (
    <MinimalWebsiteBuilderV2Provider practice={practice} theme={theme}>
      <div className="website-builder-v2-public min-h-screen bg-white">
        <HeaderSection section={headerSection} practice={practice} theme={theme} deviceType={deviceType} />

        <BlogIndexHeader
          pageTitle={pageTitle}
          postCount={posts.length}
          filteredCount={filteredPosts.length}
          activeTag={activeTag}
          tags={allTags}
          onSelectTag={selectTag}
          headingFont={headingFont}
          headingWeight={theme?.fonts?.headingWeight}
          paragraphFont={theme?.fonts?.paragraph}
          textDark={textDark}
          accentColor={accentColor}
          headerBackground={headerBackground}
        />

        <main className="mx-auto max-w-3xl px-5 pb-12 pt-4 md:pt-2">
          {filteredPosts.length === 0 ? (
            <p className="text-lg text-gray-dark">
              {activeTag ? (
                <>
                  No posts tagged &ldquo;{formatTagLabel(activeTag)}&rdquo;.{" "}
                  <button
                    type="button"
                    className="font-semibold text-teal hover:underline"
                    onClick={() => selectTag(null)}>
                    View all posts
                  </button>
                </>
              ) : (
                "No posts yet."
              )}
            </p>
          ) : (
            <div className="flex flex-col gap-10">
              {filteredPosts.map((post) => {
                const publishedLabel = formatBlogPostDate(post.publishedAt)
                return (
                  <article key={post.id} className="border-b border-gray-light pb-10">
                    <a href={scopePath(`/blog/${post.slug}`, practice)} className="group block">
                      {post.photoUrl && (
                        <img src={post.photoUrl} alt="" className="mb-4 aspect-[3/2] w-full rounded-xl object-cover" />
                      )}
                      {publishedLabel && (
                        <time
                          dateTime={post.publishedAt}
                          className="mb-2 block text-sm font-medium"
                          style={{ color: textDark, opacity: 0.7 }}>
                          {publishedLabel}
                        </time>
                      )}
                      <h2
                        className="mb-2 text-2xl font-bold group-hover:underline"
                        style={{ color: textDark, fontFamily: headingFont }}>
                        {post.title}
                      </h2>
                      {post.subtitle && (
                        <p className="mb-2 text-lg" style={{ color: textDark, opacity: 0.85 }}>
                          {post.subtitle}
                        </p>
                      )}
                      {post.tags?.length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-2">
                          {post.tags.map((tag) => (
                            <span
                              key={tag}
                              className="rounded-full px-2 py-0.5 text-xs font-medium"
                              style={tagPillStyle(textDark)}>
                              {formatTagLabel(tag)}
                            </span>
                          ))}
                        </div>
                      )}
                      {post.content && (
                        <div
                          className="wysiwyg-content line-clamp-3 text-base text-gray-dark"
                          dangerouslySetInnerHTML={{ __html: sanitize(post.content) }}
                        />
                      )}
                    </a>
                  </article>
                )
              })}
            </div>
          )}
        </main>

        <FooterSection section={footerSection} practice={practice} theme={theme} deviceType={deviceType} />
      </div>
    </MinimalWebsiteBuilderV2Provider>
  )
}

BlogIndexPage.propTypes = {
  practice: PropTypes.object.isRequired,
  posts: PropTypes.arrayOf(PropTypes.object).isRequired,
  theme: PropTypes.object,
  deviceType: PropTypes.string,
  blogPageTitle: PropTypes.string
}

export default BlogIndexPage
